import { TwilioVerifyResult } from "@/lib/DTMWebAPIClient.generated";
import { RootState } from "@/store/dtm";
import { applicantType, dateOfBirthType, existingMemberFieldsType } from "@/types/applicantType"
import { verificationCategory } from "@/types/applicationType";
import { createSlice, PayloadAction } from "@reduxjs/toolkit"

export interface applicantState {
    applicant: applicantType
}

const initialState: applicantState = {
    applicant: {
        id: 0,
        custno: 0,
        title: "",
        firstName: "",
        middleName: "",
        lastName: "",
        dob_day: "",
        dob_month: "",
        dob_year: "",
        phoneNumber: "",
        phoneType: "",
        email: "",
        maritalStatus: "",
        numOfDependents: 0,
        citizenshipStatus: "",
        firstTimeBuyer: "",
        recentResident: "",
        residentStatus: "",
        residenceYears: 0,
        residenceMonths: 0,
        jointFirstName: "",
        jointLastName: "",
        jointEmail: "",
        hasJointApp: false,
        sendFailCounter: 0,
        resendCounter: 0,
        invalidCounter: 0,
        twilioResult: {
            to: "",
            channel: "",
            checkTriesExceeded: false,
            codeVerified: false,
            sid: "",
            status: ""
        },
        sin: "",
        consentCompleted: false,
        idvId: 0,
        credentialsCreated: false,
        isProspect: true,
        alloySessionId: "",
        idVerificationStatus: verificationCategory.Pending,
        existingMemberFields: {
            isPhoneNumberAvailable: false,
            isFirstNameAvailable: false,
            isLastNameAvailable: false,
            isSinAvailable: false,
            isProvinceAvailable: false,
            isPostalCodeAvailable: false,
            isCountryAvailable: false
        },
        fmApplicationId: "",
        fmApplicantId: ""
    }
}


export const applicantSlice = createSlice({
    name: "applicant",
    initialState,
    reducers: {
        setApplicantId: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            state.applicant.id = id;
        },
        setCustNo: (state, action: PayloadAction<number>) => {
            const custno = action.payload;
            state.applicant.custno = custno;
        },
        setTitle: (state, action: PayloadAction<string>) => {
            const title = action.payload;
            state.applicant.title = title;
        },
        setFirstName: (state, action: PayloadAction<string>) => {
            const firstName = action.payload;
            state.applicant.firstName = firstName;
        },
        setMiddleName: (state, action: PayloadAction<string>) => {
            const middleName = action.payload;
            state.applicant.middleName = middleName;
        },
        setLastName: (state, action: PayloadAction<string>) => {
            const lastName = action.payload;
            state.applicant.lastName = lastName;
        },
        setDateOfBirth: (state, action: PayloadAction<dateOfBirthType>) => {
            const dob = action.payload;
            state.applicant.dob_day = dob.day;
            state.applicant.dob_month = dob.month;
            state.applicant.dob_year = dob.year;
        },
        setDateOfBirthDay: (state, action: PayloadAction<string>) => {
            const day = action.payload;
            state.applicant.dob_day = day;
        },
        setDateOfBirthMonth: (state, action: PayloadAction<string>) => {
            const month = action.payload;
            state.applicant.dob_month = month;
        },
        setDateOfBirthYear: (state, action: PayloadAction<string>) => {
            const year = action.payload;
            state.applicant.dob_year = year;
        },
        setSin: (state, action: PayloadAction<string>) => {
            const sin = action.payload;
            state.applicant.sin = sin;
        },
        setPhoneNumber: (state, action: PayloadAction<string>) => {
            const phoneNumber = action.payload;
            state.applicant.phoneNumber = phoneNumber;
        },
        setPhoneType: (state, action: PayloadAction<string>) => {
            const phoneType = action.payload;
            state.applicant.phoneType = phoneType;
        },
        setEmailAddress: (state, action: PayloadAction<string>) => {
            const email = action.payload;
            state.applicant.email = email;
        },
        setMaritalStatus: (state, action: PayloadAction<string>) => {
            const maritalStatus = action.payload;
            state.applicant.maritalStatus = maritalStatus;
        },
        setNumOfDependents: (state, action: PayloadAction<number>) => {
            const dependents = action.payload;
            state.applicant.numOfDependents = dependents;
        },
        setCitizenshipStatus: (state, action: PayloadAction<string>) => {
            const status = action.payload;
            state.applicant.citizenshipStatus = status;
        },
        setFirstTimeBuyer: (state, action: PayloadAction<string>) => {
            const buyer = action.payload;
            state.applicant.firstTimeBuyer = buyer;
        },
        setRecentResident: (state, action: PayloadAction<string>) => {
            const recent = action.payload;
            state.applicant.recentResident = recent;
        },
        setResidentStatus: (state, action: PayloadAction<string>) => {
            const status = action.payload;
            state.applicant.residentStatus = status;
        },
        setResidenceYears: (state, action: PayloadAction<number>) => {
            const years = action.payload;
            state.applicant.residenceYears = years;
        },
        setResidenceMonths: (state, action: PayloadAction<number>) => {
            const months = action.payload;
            state.applicant.residenceMonths = months;
        },

        setJointFirstName: (state, action: PayloadAction<string>) => {
            const jointFirstName = action.payload;
            state.applicant.jointFirstName = jointFirstName;
        },
        setJointLastName: (state, action: PayloadAction<string>) => {
            const jointLastName = action.payload;
            state.applicant.jointLastName = jointLastName;
        },
        setJointEmail: (state, action: PayloadAction<string>) => {
            const jointEmail = action.payload;
            state.applicant.jointEmail = jointEmail;
        },
        setHasJointApp: (state, action: PayloadAction<boolean>) => {
            const hasJointApp = action.payload;
            state.applicant.hasJointApp = hasJointApp;
        },
        setTwilioResultCodeVerified: (state, action: PayloadAction<boolean>) => {
            const codeVerified = action.payload;
            state.applicant.twilioResult.codeVerified = codeVerified;
        },
        setTwilioResult: (state, action: PayloadAction<TwilioVerifyResult>) => {
            const twilioResult = action.payload;
            state.applicant.twilioResult.channel = (twilioResult.channel != null) ? twilioResult.channel : "";
            state.applicant.twilioResult.checkTriesExceeded = (twilioResult.checkTriesExceeded != null) ? twilioResult.checkTriesExceeded : false;
            state.applicant.twilioResult.codeVerified = (twilioResult.codeVerified != null) ? twilioResult.codeVerified : false;
            state.applicant.twilioResult.sid = (twilioResult.sid != null) ? twilioResult.sid : "";
            state.applicant.twilioResult.status = (twilioResult.status != null) ? twilioResult.status : "";
            state.applicant.twilioResult.to = (twilioResult.to != null) ? twilioResult.to : "";

        },
        setSendFailCounter: (state, action: PayloadAction<number>) => {
            const sendFailCounter = action.payload;
            state.applicant.sendFailCounter = sendFailCounter;
        },
        setResendCounter: (state, action: PayloadAction<number>) => {
            const resendCounter = action.payload;
            state.applicant.resendCounter = resendCounter;
        },
        setInvalidCounter: (state, action: PayloadAction<number>) => {
            const invalidCounter = action.payload;
            state.applicant.invalidCounter = invalidCounter;
        },
        setConsentCompleted: (state, action: PayloadAction<boolean>) => {
            const consent = action.payload;
            state.applicant.consentCompleted = consent;
        },
        setCredentialsCreated: (state, action: PayloadAction<boolean>) => {
            const created = action.payload;
            state.applicant.credentialsCreated = created;
        },
        setIsProspect: (state, action: PayloadAction<boolean>) => {
            const prospect = action.payload;
            state.applicant.isProspect = prospect;
        },
        clearJointState: (state) => {
            state.applicant.jointFirstName = "";
            state.applicant.jointLastName = "";
            state.applicant.jointEmail = "";
            state.applicant.hasJointApp = false;
        },
        setIdvId: (state, action: PayloadAction<number>) => {
            state.applicant.idvId = action.payload;
        },
        setAlloySessionId: (state, action: PayloadAction<string | undefined>) => {
            state.applicant.alloySessionId = action.payload
        },
        setIdVerificationStatus: (state, action: PayloadAction<verificationCategory>) => {
            state.applicant.idVerificationStatus = action.payload
        },
        setExistingMemberFields: (state, action: PayloadAction<existingMemberFieldsType>) => {
            state.applicant.existingMemberFields = action.payload;
        },
        setFmApplicationId: (state, action: PayloadAction<string>) => {
            state.applicant.fmApplicationId = action.payload;
        },
        setFmApplicantId: (state, action: PayloadAction<string>) => {
            state.applicant.fmApplicantId = action.payload;
        },
        clearApplicantState: (state) => {
            state.applicant.id = 0;
            state.applicant.custno = 0;
            state.applicant.title = "";
            state.applicant.firstName = "";
            state.applicant.middleName = "";
            state.applicant.lastName = "";
            state.applicant.email = "";
            state.applicant.dob_day = "";
            state.applicant.dob_month = "";
            state.applicant.dob_year = "";
            state.applicant.sin = "",
                state.applicant.phoneNumber = "";
            state.applicant.phoneType = "";
            state.applicant.maritalStatus = "",
                state.applicant.numOfDependents = 0,
                state.applicant.citizenshipStatus = "",
                state.applicant.firstTimeBuyer = "",
                state.applicant.recentResident = "",
                state.applicant.residentStatus = "",
                state.applicant.residenceYears = 0,
                state.applicant.residenceMonths = 0,
                state.applicant.jointFirstName = "";
            state.applicant.jointLastName = "";
            state.applicant.jointEmail = "";
            state.applicant.twilioResult.channel = "";
            state.applicant.twilioResult.checkTriesExceeded = false;
            state.applicant.twilioResult.codeVerified = false;
            state.applicant.twilioResult.sid = "";
            state.applicant.twilioResult.status = "";
            state.applicant.twilioResult.to = "";
            state.applicant.consentCompleted = false;
            state.applicant.idvId = 0
            state.applicant.credentialsCreated = false;
            state.applicant.isProspect = true;
            state.applicant.alloySessionId = "";
            state.applicant.idVerificationStatus = verificationCategory.Pending;
            state.applicant.existingMemberFields = {
                isFirstNameAvailable: false,
                isLastNameAvailable: false,
                isPhoneNumberAvailable: false,
                isSinAvailable: false,
                isProvinceAvailable: false,
                isPostalCodeAvailable: false,
                isCountryAvailable: false
            },
            state.applicant.fmApplicationId = "",
            state.applicant.fmApplicantId = ""
        },
        clearTwilioResultCodeVerified: (state) => {
            state.applicant.twilioResult.codeVerified = false;
        },
        clearVerificationCounters: (state) => {
            state.applicant.sendFailCounter = 0;
            state.applicant.resendCounter = 0;
            state.applicant.invalidCounter = 0;
        },
    }
});

export const { setApplicantId, setCustNo, setTitle, setFirstName, setMiddleName, setLastName, setDateOfBirthYear, setDateOfBirthMonth, setDateOfBirthDay, setPhoneNumber, setPhoneType, setMaritalStatus,
    setNumOfDependents, setCitizenshipStatus, setFirstTimeBuyer, setRecentResident,
    setResidentStatus, setResidenceYears, setResidenceMonths, setIsProspect, setEmailAddress, setAlloySessionId,
    clearApplicantState, setJointEmail, setJointFirstName, setJointLastName, setHasJointApp, clearJointState, setTwilioResultCodeVerified,
    clearTwilioResultCodeVerified, setResendCounter, setInvalidCounter, clearVerificationCounters, setSendFailCounter, setSin, setTwilioResult, setConsentCompleted, setIdvId,
    setCredentialsCreated, setIdVerificationStatus, setExistingMemberFields, setFmApplicationId, setFmApplicantId
} = applicantSlice.actions

export default applicantSlice.reducer

export const selectApplicant = (state: RootState) => state.applicant;

